/**
 * BILAN DE FIN D'ESSAI — lecture et calcul
 *
 * Principe (audit §0.3) : ne montrer que des VALEURS ABSOLUES mesurées sur les
 * 7 jours, jamais une comparaison ni un verdict qui supposerait une référence
 * que 7 jours ne donnent pas.
 *
 * Sont donc volontairement ABSENTS, et ne doivent pas être rajoutés ici :
 *   • ACWR         — demande 28 jours de charge (fenêtre chronique)
 *   • Strain       — demande une semaine complète et une monotonie de référence
 *   • État de forme — combine ACWR, Strain et Hooper
 *   • Score de progression — pondère régularité, Intensité et Volume, tous
 *     relatifs à une semaine précédente
 *   • Tendances Intensité / Volume — divisent deux semaines de programme ; la
 *     première semaine n'a pas de précédente, un « % » y serait inventé
 *
 * Ce qui reste est mesuré, pas interprété : des sommes, des moyennes et un
 * décompte.
 *
 * Compte en SUIVI SEUL (CONTEXTE §13E) : aucune séance Zeyone n'existe, le bilan
 * se calcule sur ses séances libres (`calendar_events`, réponse au questionnaire
 * dans `meta.post_session`). Pas de « séances prévues » : toutes les séances libres
 * de la période comptent comme réalisées, questionnaire rempli ou non (sauf « Je
 * n'ai pas fait cette séance ») ; durée, RPE et charge ne viennent que de celles
 * dont le questionnaire est rempli. La charge totale est leur somme — pas la table
 * `training_load`, qui additionne aussi activités sportives et récupérations.
 */

import {
  supabase,
  getActivityPostSessionAnswer,
  type CalendarEventRow,
  type TrackingMode,
  type DailyWellnessRow,
  type SessionFeedbackRow,
  type SessionSetLogRow,
  type TrainingLoadRow,
  type WeightHistoryRow,
  type RmHistoryRow,
  type StrengthBlockTargetRow,
} from "@/lib/supabase"
import { sumAbsoluteWork } from "@/lib/monitoring/calculations"
import type { StrengthMovement } from "@/lib/strengthStandards"

/**
 * Le score Hooper moyen n'est affiché qu'à partir de ce nombre de relevés.
 * En dessous, une « moyenne » sur un ou deux matins dirait surtout le jour où
 * le client a pensé à remplir — pas son état de la semaine.
 */
export const MIN_HOOPER_ENTRIES = 3

/**
 * Deux débuts de fenêtre, et ce n'est pas un détail.
 *
 * Le questionnaire du matin ne part qu'à partir de J1 (D12 : 7 matins, de J1 à
 * J7) — le jour de l'inscription, le client vient déjà de tout remplir. Le
 * décompte Hooper part donc de J1, et son dénominateur est 7.
 *
 * Le TRAVAIL, lui, commence à l'activation. Un client qui s'entraîne le soir de
 * son inscription a fait cette séance pendant son essai : la compter à partir de
 * J1 la ferait purement et simplement disparaître du bilan, avec sa charge, son
 * temps sous tension et son RPE.
 */
export interface TrialWindow {
  /** J0, l'activation. Début du décompte du travail (séances, charge, RPE). */
  workFromDay: string
  /** J1, premier matin de questionnaire. Début du seul décompte Hooper. */
  hooperFromDay: string
  /** J7, dernier jour d'entraînement de l'essai (veille du matin de J8). */
  toDay: string
  /** J8, jour du bilan et de la pesée. */
  reportDay: string
}

export interface TrialSessionEntry {
  date: string
  title: string
  done: boolean
  durationMinutes: number | null
  rpe: number | null
}

export interface TrialHooper {
  entries: number
  /** Moyenne sur 28 (4 items notés 1 à 7), ou null sous MIN_HOOPER_ENTRIES. */
  averageScore: number | null
  averageSleep: number | null
  averageFreshness: number | null
  averageMood: number | null
  averageSoreness: number | null
  bestDay: { date: string; score: number } | null
  worstDay: { date: string; score: number } | null
}

export interface TrialWeight {
  /** Poids du diagnostic (wizard), point de départ. */
  startKg: number | null
  /** Pesée du matin de J8, si elle a été saisie. */
  reportKg: number | null
  deltaKg: number | null
}

export interface TrialStrengthEntry {
  movement: StrengthMovement
  measuredKg: number
  measuredOn: string
  targetKg: number | null
}

export interface TrialReport {
  window: TrialWindow
  /** `suivi_seul` : séances libres, sans « prévues » (voir l'en-tête du fichier). */
  mode: TrackingMode
  sessionsDone: number
  /** Séances Zeyone planifiées. Sans objet en suivi seul (égal à sessionsDone). */
  sessionsPlanned: number
  /** Suivi seul : séances réalisées dont le questionnaire n'a pas été rempli. */
  sessionsWithoutQuestionnaire: number
  sessions: TrialSessionEntry[]
  totalDurationMinutes: number
  /** Charge soulevée, en kg (somme d'Intensité). */
  intensityKg: number
  /** Temps sous tension, en secondes (somme de Volume). */
  volumeSeconds: number
  /** Part des séries réellement comptées dans les deux sommes ci-dessus. */
  intensityCoveragePct: number
  volumeCoveragePct: number
  averageRpe: number | null
  /** Charge d'entraînement totale, en UA (RPE × durée, méthode sRPE de Foster). */
  totalLoadAu: number
  hooper: TrialHooper
  weight: TrialWeight
  strength: TrialStrengthEntry[]
}

/** YYYY-MM-DD d'un instant, vu de Paris — même convention que les edge functions. */
function parisDay(instant: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant)
}

function shiftDay(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

const PARIS_CLOCK = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

/**
 * L'instant UTC de minuit, heure de Paris, pour un jour donné.
 *
 * `completed_at` est un timestamptz : le filtrer suppose des bornes en instants,
 * pas en jours. Un décalage écrit en dur (« +02:00 ») décalerait d'une heure la
 * moitié de l'année et ferait entrer la dernière heure de la veille dans le bilan.
 *
 * Paris est à UTC+1 ou UTC+2 : minuit local est 22:00Z ou 23:00Z la veille. On
 * essaie les deux et on garde celui que le fuseau relit bien comme minuit —
 * aucune table de décalages à tenir à jour.
 */
function parisMidnightIso(day: string): string {
  const [y, m, d] = day.split("-").map(Number)
  for (const offset of [2, 1]) {
    const candidate = new Date(Date.UTC(y, m - 1, d, -offset))
    const local = PARIS_CLOCK.format(candidate)
    // "2026-09-23, 00:00" — le jour attendu, à minuit (24:00 selon les moteurs).
    if (local.startsWith(day) && /\b(00|24):00$/.test(local)) return candidate.toISOString()
  }
  // Nuit du passage à l'heure d'été : le saut a lieu à 2h, minuit existe toujours.
  return new Date(Date.UTC(y, m - 1, d, -1)).toISOString()
}

/**
 * La fenêtre du bilan, déduite des deux dates de l'essai.
 *
 * `trial_ends_at` est posée au matin de J8 à 07:00 Paris : le jour du bilan est
 * donc son jour parisien, et J7 la veille. J1 est le lendemain de l'activation.
 * On ne calcule pas J7 comme « J1 + 6 » : les deux bornes viennent chacune de la
 * donnée, ce qui reste juste même si un essai a été prolongé à la main.
 */
export function trialWindow(startedAt: string, endsAt: string): TrialWindow {
  const reportDay = parisDay(new Date(endsAt))
  const workFromDay = parisDay(new Date(startedAt))
  return {
    workFromDay,
    hooperFromDay: shiftDay(workFromDay, 1),
    toDay: shiftDay(reportDay, -1),
    reportDay,
  }
}

function average(values: number[]): number | null {
  if (values.length === 0) return null
  return values.reduce((s, v) => s + v, 0) / values.length
}

export async function fetchTrialReport(
  userId: string,
  startedAt: string,
  endsAt: string,
): Promise<TrialReport> {
  const window = trialWindow(startedAt, endsAt)
  const { workFromDay, hooperFromDay, toDay, reportDay } = window

  // Bornes en instants pour les colonnes timestamptz (séries, questionnaires) :
  // de minuit à J0 à minuit à J8, heure de Paris. Une séance faite tard le soir
  // de J7 compte — c'est toute la raison de l'échéance au matin de J8 (D12).
  const fromInstant = parisMidnightIso(workFromDay)
  const toInstant = parisMidnightIso(reportDay)

  const [eventsRes, freeRes, feedbacksRes, loadsRes, logsRes, wellnessRes, wizardRes, weightRes, rmRes, targetsRes] =
    await Promise.all([
      supabase
        .from("calendar_events")
        .select("event_date, title, type, programme_session_number")
        .eq("user_id", userId)
        .in("type", ["seance-planned", "seance-done"])
        .gte("event_date", workFromDay)
        .lte("event_date", toDay)
        .order("event_date", { ascending: true }),
      // Séances libres : seules séances d'un compte en suivi seul. Même fenêtre que le
      // travail (J0 → J7), jour de la séance et non jour de la réponse.
      supabase
        .from("calendar_events")
        .select("event_date, end_time, title, meta")
        .eq("user_id", userId)
        .eq("type", "seance-libre")
        .gte("event_date", workFromDay)
        .lte("event_date", toDay)
        .order("event_date", { ascending: true })
        .order("end_time", { ascending: true }),
      supabase
        .from("session_feedback")
        .select("*")
        .eq("user_id", userId)
        .gte("completed_at", fromInstant)
        .lt("completed_at", toInstant),
      supabase
        .from("training_load")
        .select("*")
        .eq("user_id", userId)
        .gte("date", workFromDay)
        .lte("date", toDay),
      supabase
        .from("session_set_logs")
        .select("*")
        .eq("user_id", userId)
        .gte("completed_at", fromInstant)
        .lt("completed_at", toInstant),
      supabase
        // Hooper seulement : de J1 à J7, sept matins, dénominateur affiché 7.
        .from("daily_wellness")
        .select("*")
        .eq("user_id", userId)
        .gte("date", hooperFromDay)
        .lte("date", toDay)
        .order("date", { ascending: true }),
      // `*` plutôt qu'une liste : `tracking_mode` n'existe qu'après la migration
      // 20261018120000, et une colonne absente ferait échouer toute la lecture (poids
      // de départ compris).
      supabase
        .from("wizard_submissions")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle(),
      // Exactement la pesée du jour du bilan (J8). Une pesée plus tardive n'est
      // pas « la pesée de fin d'essai » : c'est le lendemain d'autre chose, et
      // l'écart au poids de départ ne se lirait plus sur 7 jours. C'est aussi la
      // date qu'écrit le champ de pesée de l'écran de fin d'essai.
      supabase
        .from("weight_history")
        .select("*")
        .eq("user_id", userId)
        .eq("date", reportDay)
        .limit(1),
      supabase
        .from("rm_history")
        .select("*")
        .eq("user_id", userId)
        .gte("measured_on", workFromDay)
        .lte("measured_on", toDay)
        .order("measured_on", { ascending: true }),
      supabase
        .from("strength_block_targets")
        .select("*")
        .eq("user_id", userId)
        .order("block_start_date", { ascending: false }),
    ])

  const events = (eventsRes.data ?? []) as {
    event_date: string
    title: string
    type: string
    programme_session_number: number | null
  }[]
  const feedbacks = (feedbacksRes.data ?? []) as SessionFeedbackRow[]
  const loads = (loadsRes.data ?? []) as TrainingLoadRow[]
  const logs = (logsRes.data ?? []) as SessionSetLogRow[]
  const wellness = (wellnessRes.data ?? []) as DailyWellnessRow[]
  const weightRows = (weightRes.data ?? []) as WeightHistoryRow[]
  const rms = (rmRes.data ?? []) as RmHistoryRow[]

  // Une séance « faite » l'est parce que son questionnaire de fin a été rempli
  // (session_feedback), pas parce que l'événement a basculé en seance-done : la
  // bascule peut échouer, le questionnaire est ce qui déclenche tout le reste.
  const feedbackByDay = new Map<string, SessionFeedbackRow>()
  for (const f of feedbacks) feedbackByDay.set(parisDay(new Date(f.completed_at)), f)

  const programmeSessions: TrialSessionEntry[] = events.map((e) => {
    const feedback = feedbackByDay.get(e.event_date)
    const done = e.type === "seance-done" || feedback !== undefined
    return {
      date: e.event_date,
      title: e.title,
      done,
      durationMinutes: feedback?.actual_duration_minutes ?? null,
      rpe: feedback?.rpe ?? null,
    }
  })

  const wizard = wizardRes.data as { weight: unknown; weight_start: unknown; tracking_mode?: TrackingMode } | null
  const mode: TrackingMode = wizard?.tracking_mode === "suivi_seul" ? "suivi_seul" : "programme"

  // Suivi seul : séances libres réalisées (questionnaire rempli ou pas, « Je n'ai pas
  // fait cette séance » exclue).
  const freeAnswers = ((freeRes.data ?? []) as Pick<CalendarEventRow, "event_date" | "end_time" | "title" | "meta">[])
    .map((e) => ({ event: e, answer: getActivityPostSessionAnswer(e) }))
    .filter(({ answer }) => answer?.status !== "skipped")
  const freeDone = freeAnswers
    .map(({ answer }) => (answer?.status === "done" ? answer : null))
    .filter((a): a is Extract<NonNullable<typeof a>, { status: "done" }> => a !== null)
  const freeSessions: TrialSessionEntry[] = freeAnswers.map(({ event, answer }) => ({
    date: event.event_date,
    title: event.title,
    done: true,
    durationMinutes: answer?.status === "done" ? answer.duration : null,
    rpe: answer?.status === "done" ? answer.rpe : null,
  }))

  const suiviSeul = mode === "suivi_seul"
  const sessions = suiviSeul ? freeSessions : programmeSessions
  const rpes = suiviSeul
    ? freeDone.map((a) => a.rpe)
    : feedbacks.map((f) => f.rpe).filter((r): r is number => r !== null)
  const work = sumAbsoluteWork(logs)

  const hooperScores = wellness.map((w) => w.hooper_score)
  const sortedByScore = [...wellness].sort((a, b) => a.hooper_score - b.hooper_score)
  const enough = wellness.length >= MIN_HOOPER_ENTRIES

  // Cibles de force : celles du bloc en cours (le plus récent), une par mouvement.
  const targetRows = ((targetsRes.data ?? []) as Record<string, unknown>[]).map((r) => ({
    ...r,
    target_value: r.target_value == null ? null : Number(r.target_value),
  })) as unknown as StrengthBlockTargetRow[]
  const latestBlock = targetRows[0]?.block_start_date ?? null
  const targetByMovement = new Map<StrengthMovement, number>()
  for (const t of targetRows) {
    if (t.block_start_date !== latestBlock) continue
    if (!targetByMovement.has(t.movement)) targetByMovement.set(t.movement, t.target_value)
  }

  // Un seul point par mouvement : le plus récent mesuré pendant l'essai.
  const lastRmByMovement = new Map<StrengthMovement, RmHistoryRow>()
  for (const r of rms) lastRmByMovement.set(r.movement, r)

  // PostgREST sérialise les colonnes `numeric` en CHAÎNES (dette connue, corrigée
  // au cas par cas depuis body_measurements) : sans Number(), « 72.4 - 71 » ferait
  // une soustraction correcte par coercition mais « 72.4 » ne serait pas un nombre
  // pour toFixed ni pour une comparaison stricte.
  const num = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v))

  const startKg = num(wizard?.weight_start) ?? num(wizard?.weight)
  const reportKg = num(weightRows[0]?.weight)

  return {
    window,
    mode,
    sessionsDone: sessions.filter((s) => s.done).length,
    sessionsPlanned: sessions.length,
    sessionsWithoutQuestionnaire: suiviSeul ? freeSessions.length - freeDone.length : 0,
    sessions,
    totalDurationMinutes: suiviSeul
      ? freeDone.reduce((s, a) => s + a.duration, 0)
      : feedbacks.reduce((s, f) => s + (f.actual_duration_minutes ?? 0), 0),
    intensityKg: work.intensityKg,
    volumeSeconds: work.volumeSeconds,
    intensityCoveragePct: work.intensityCoverage.coveragePct,
    volumeCoveragePct: work.volumeCoverage.coveragePct,
    averageRpe: average(rpes),
    totalLoadAu: suiviSeul
      ? freeDone.reduce((s, a) => s + a.training_load, 0)
      : loads.reduce((s, l) => s + l.training_load, 0),
    hooper: {
      entries: wellness.length,
      averageScore: enough ? average(hooperScores) : null,
      averageSleep: enough ? average(wellness.map((w) => w.sleep_quality)) : null,
      averageFreshness: enough ? average(wellness.map((w) => w.muscle_freshness)) : null,
      averageMood: enough ? average(wellness.map((w) => w.mood)) : null,
      averageSoreness: enough ? average(wellness.map((w) => w.soreness)) : null,
      // Hooper : plus le score est BAS, meilleure est la forme (4 = au mieux).
      bestDay: enough && sortedByScore[0]
        ? { date: sortedByScore[0].date, score: sortedByScore[0].hooper_score }
        : null,
      worstDay: enough && sortedByScore.length > 1
        ? {
            date: sortedByScore[sortedByScore.length - 1].date,
            score: sortedByScore[sortedByScore.length - 1].hooper_score,
          }
        : null,
    },
    weight: {
      startKg,
      reportKg,
      deltaKg: startKg !== null && reportKg !== null ? reportKg - startKg : null,
    },
    strength: [...lastRmByMovement.values()].map((r) => ({
      movement: r.movement,
      measuredKg: Number(r.value),
      measuredOn: r.measured_on,
      targetKg: targetByMovement.get(r.movement) ?? null,
    })),
  }
}
